import { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { Instagram, Youtube, Mail, X } from 'lucide-react';
import apiClient, { getSiteCategories, getSiteContent } from '@/lib/api';
import { FormattedText } from '@/lib/formatters';
import { toast } from 'sonner';

const TikTokIcon = () => (
  <svg 
    className="w-5 h-5" 
    viewBox="0 0 24 24" 
    fill="currentColor"
  >
    <path d="M19.59 6.69a4.83 4.83 0 01-3.77-4.25V2h-3.45v13.67a2.89 2.89 0 01-5.2 1.74 2.89 2.89 0 012.31-4.64 2.93 2.93 0 01.88.13V9.4a6.84 6.84 0 00-1-.05A6.33 6.33 0 005 20.1a6.34 6.34 0 0010.86-4.43v-7a8.16 8.16 0 004.77 1.52v-3.4a4.85 4.85 0 01-1-.1z"/>
  </svg>
);

const PinterestIcon = () => (
  <svg className="w-5 h-5" viewBox="0 0 24 24" fill="currentColor">
    <path d="M12 0c-6.627 0-12 5.372-12 12 0 5.084 3.163 9.426 7.627 11.174-.105-.949-.2-2.405.042-3.441.218-.937 1.407-5.965 1.407-5.965s-.359-.719-.359-1.782c0-1.668.967-2.914 2.171-2.914 1.023 0 1.518.769 1.518 1.69 0 1.029-.655 2.568-.994 3.995-.283 1.194.599 2.169 1.777 2.169 2.133 0 3.772-2.249 3.772-5.495 0-2.873-2.064-4.882-5.012-4.882-3.414 0-5.418 2.561-5.418 5.207 0 1.031.397 2.138.893 2.738.098.119.112.224.083.345l-.333 1.36c-.053.22-.174.267-.402.161-1.499-.698-2.436-2.889-2.436-4.649 0-3.785 2.75-7.262 7.929-7.262 4.163 0 7.398 2.967 7.398 6.931 0 4.136-2.607 7.464-6.227 7.464-1.216 0-2.359-.631-2.75-1.378l-.748 2.853c-.271 1.043-1.002 2.35-1.492 3.146 1.124.347 2.317.535 3.554.535 6.627 0 12-5.373 12-12 0-6.628-5.373-12-12-12z"/>
  </svg>
);

const FacebookIcon = () => (
  <svg className="w-5 h-5" viewBox="0 0 24 24" fill="currentColor">
    <path d="M24 12.073c0-6.627-5.373-12-12-12s-12 5.373-12 12c0 5.99 4.388 10.954 10.125 11.854v-8.385H7.078v-3.47h3.047V9.43c0-3.007 1.792-4.669 4.533-4.669 1.312 0 2.686.235 2.686.235v2.953H15.83c-1.491 0-1.956.925-1.956 1.874v2.25h3.328l-.532 3.47h-2.796v8.385C19.612 23.027 24 18.062 24 12.073z"/>
  </svg>
);

const SubstackIcon = () => (
  <svg className="w-5 h-5" viewBox="0 0 24 24" fill="currentColor">
    <path d="M22.539 8.242H1.46V5.406h21.08v2.836zM1.46 10.812V24L12 18.11 22.54 24V10.812H1.46zM22.54 0H1.46v2.836h21.08V0z"/>
  </svg>
);

const DEFAULT_FOOTER_CONTENT = {
  brand_name: 'The Cool-Girl Cut',
  brand_description: 'My favorite Amazon finds — the pieces I actually love, style, and obsess over. Real-life outfits, AI-inspired fashion edits, and effortless cool-girl energy.',
  creator_section_title: 'Follow the creator',
  social_tiktok_url: 'https://tiktok.com/@the.cool.girl.cut',
  social_instagram_url: 'https://instagram.com/teresarcordeiro',
  social_facebook_url: '',
  social_pinterest_url: 'https://pinterest.com/thecoolgirlcut',
  social_youtube_url: 'https://youtube.com/@teresarcordeiro',
  social_substack_url: '',
  newsletter_enabled: true,
  copyright_text: '© 2026 THE COOL-GIRL CUT. ALL RIGHTS RESERVED.',
  affiliate_disclosure: 'AS AN AMAZON UK & US ASSOCIATE, WE EARN FROM QUALIFYING PURCHASES.',
  additional_links: [
    { label: 'Shop All Products', url: '/search', active: true },
    { label: 'Search', url: '/search', active: true },
    { label: 'About Us', url: '/about', active: true },
    { label: 'Affiliate Disclosure', url: '/affiliate-disclosure', active: true },
    { label: 'Contact', url: '', active: false },
    { label: 'Privacy Policy', url: '/privacy-policy', active: true }
  ]
};

const Footer = () => {
  const [categories, setCategories] = useState([]);
  const [content, setContent] = useState(DEFAULT_FOOTER_CONTENT);
  const [showNewsletterModal, setShowNewsletterModal] = useState(false);
  const [email, setEmail] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [newsletterFeatureEnabled, setNewsletterFeatureEnabled] = useState(true);

  useEffect(() => {
    loadData();
  }, []);

  const loadData = async () => {
    try {
      // Use deduplicated and cached API calls
      const [categoriesData, siteContentData, newsletterStatus] = await Promise.all([
        getSiteCategories(),
        getSiteContent().catch(() => null),
        apiClient.get('/newsletter/status').catch(() => ({ data: { enabled: true } }))
      ]);
      setCategories(categoriesData);
      
      // Set newsletter feature status (from admin settings)
      setNewsletterFeatureEnabled(newsletterStatus.data.enabled);
      
      // Merge site content with defaults
      if (siteContentData?.footer) {
        setContent(prev => ({ ...prev, ...siteContentData.footer }));
      }
    } catch (error) {
      // Fallback to defaults if API fails
      setCategories([
        { slug: 'fashion', title: "Women's Fashion", subcategories: [] },
        { slug: 'beauty', title: 'Beauty', subcategories: [] },
        { slug: 'lifestyle', title: 'Lifestyle', subcategories: [] },
      ]);
    }
  };

  const handleNewsletterSubmit = async (e) => {
    e.preventDefault();
    if (!email.trim()) {
      toast.error('Please enter your email address');
      return;
    }
    
    setIsSubmitting(true);
    try {
      await apiClient.post('/newsletter/subscribe', { email: email.trim() });
      toast.success('Thank you for subscribing!');
      setEmail('');
      setShowNewsletterModal(false);
    } catch (error) {
      if (error.response?.status === 409) {
        toast.info('You are already subscribed!');
      } else {
        toast.error('Something went wrong. Please try again.');
      }
    } finally {
      setIsSubmitting(false);
    }
  };

  // Filter to only show active categories that should appear in footer
  const activeCategories = categories.filter(cat => cat.active !== false && cat.show_in_footer !== false);

  return (
    <footer className="bg-site-bg-dark text-site-text-inverse">
      {/* Main Footer */}
      <div className="px-4 md:px-8 pt-8 pb-0">
        {/* Two-column layout: Left (brand+social) | Right (categories) */}
        <div className="flex flex-col lg:flex-row lg:gap-16">
          {/* Left column - width controlled by content (description or social icons) */}
          <div className="flex-shrink-0 lg:max-w-[420px]">
            <h2 className="font-heading text-3xl md:text-4xl italic mb-4">
              {content.brand_name}
            </h2>
            <p className="font-body text-sm text-site-text-subtle leading-relaxed mb-4 [&_strong]:font-semibold [&_strong]:text-site-text-inverse [&_em]:italic">
              <FormattedText>{content.brand_description}</FormattedText>
            </p>
            <p className="font-heading text-lg italic text-site-text-inverse mb-4">
              {content.creator_section_title}
            </p>
            <div className="flex items-center gap-3 flex-wrap">
              {content.social_tiktok_url && (
                <a
                  href={content.social_tiktok_url}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="p-3 border border-site-text-inverse hover:bg-site-text-inverse hover:text-site-bg-dark transition-colors"
                  data-testid="footer-tiktok-link"
                  title="TikTok"
                >
                  <TikTokIcon />
                </a>
              )}
              {content.social_instagram_url && (
                <a
                  href={content.social_instagram_url}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="p-3 border border-site-text-inverse hover:bg-site-text-inverse hover:text-site-bg-dark transition-colors"
                  data-testid="footer-instagram-link"
                  title="Instagram"
                >
                  <Instagram className="w-5 h-5" />
                </a>
              )}
              {content.social_facebook_url && (
                <a
                  href={content.social_facebook_url}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="p-3 border border-site-text-inverse hover:bg-site-text-inverse hover:text-site-bg-dark transition-colors"
                  data-testid="footer-facebook-link"
                  title="Facebook"
                >
                  <FacebookIcon />
                </a>
              )}
              {content.social_pinterest_url && (
                <a
                  href={content.social_pinterest_url}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="p-3 border border-site-text-inverse hover:bg-site-text-inverse hover:text-site-bg-dark transition-colors"
                  data-testid="footer-pinterest-link"
                  title="Pinterest"
                >
                  <PinterestIcon />
                </a>
              )}
              {content.social_youtube_url && (
                <a
                  href={content.social_youtube_url}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="p-3 border border-site-text-inverse hover:bg-site-text-inverse hover:text-site-bg-dark transition-colors"
                  data-testid="footer-youtube-link"
                  title="YouTube"
                >
                  <Youtube className="w-5 h-5" />
                </a>
              )}
              {content.social_substack_url && (
                <a
                  href={content.social_substack_url}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="p-3 border border-site-text-inverse hover:bg-site-text-inverse hover:text-site-bg-dark transition-colors"
                  data-testid="footer-substack-link"
                  title="Substack"
                >
                  <SubstackIcon />
                </a>
              )}
              {content.newsletter_enabled && newsletterFeatureEnabled && (
                <button
                  onClick={() => setShowNewsletterModal(true)}
                  className="p-3 border border-site-text-inverse hover:bg-site-text-inverse hover:text-site-bg-dark transition-colors"
                  data-testid="footer-newsletter-btn"
                  title="Subscribe to Newsletter"
                >
                  <Mail className="w-5 h-5" />
                </button>
              )}
            </div>
          </div>

          {/* Right: Categories - starts at top on lg, wraps to max 2 rows, moves below on smaller screens */}
          <div className="mt-8 lg:mt-0 flex-1">
            <div className="flex flex-wrap gap-x-8 lg:gap-x-12 gap-y-6">
              {activeCategories.map((category) => {
                const visibleSubcategories = (category.subcategories || []).filter(sub => sub.active !== false);
                
                return (
                  <div key={category.slug} className="min-w-0 flex-shrink-0">
                    <Link 
                      to={`/${category.slug}`}
                      className="font-mono text-xs uppercase tracking-widest mb-4 block hover:text-site-accent-blue transition-colors whitespace-nowrap"
                      data-testid={`footer-${category.slug}-link`}
                    >
                      {category.title}
                    </Link>
                    <nav className="flex flex-col space-y-2">
                      {visibleSubcategories.map((sub) => (
                        <Link 
                          key={sub.id}
                          to={`/${category.slug}/${sub.slug || sub.id}`} 
                          className="font-body text-sm text-site-text-subtle hover:text-site-text-inverse transition-colors whitespace-nowrap"
                          data-testid={`footer-${sub.id}`}
                        >
                          {sub.name}
                        </Link>
                      ))}
                    </nav>
                  </div>
                );
              })}
            </div>
          </div>
        </div>

        {/* Additional Links Row */}
        <div className="mt-8 py-2 border-t border-zinc-800 flex flex-wrap items-center gap-6 md:gap-10">
          {(content.additional_links || []).filter(link => link.active).map((link, index) => {
            if (link.url) {
              return (
                <Link 
                  key={index}
                  to={link.url}
                  className="font-body text-sm text-site-text-subtle hover:text-site-text-inverse transition-colors"
                  data-testid={`footer-link-${index}`}
                >
                  {link.label}
                </Link>
              );
            }
            return (
              <span key={index} className="font-body text-sm text-zinc-600" data-testid={`footer-link-${index}`}>
                {link.label}
              </span>
            );
          })}
        </div>
      </div>

      {/* Bottom Bar - extra padding bottom for "Made with Emergent" badge on mobile */}
      <div className="border-t border-zinc-800 px-4 md:px-8 py-6 pb-20 md:pb-6">
        <p className="font-mono text-xs text-site-text-muted text-center md:text-left">
          {content.copyright_text}
          <span className="mx-2">|</span>
          {content.affiliate_disclosure}
        </p>
      </div>

      {/* Newsletter Modal */}
      {showNewsletterModal && (
        <div className="fixed inset-0 bg-black/70 z-50 flex items-center justify-center p-4" onClick={() => setShowNewsletterModal(false)}>
          <div 
            className="bg-site-bg-dark border border-zinc-800 max-w-md w-full p-8 relative"
            onClick={(e) => e.stopPropagation()}
          >
            <button
              onClick={() => setShowNewsletterModal(false)}
              className="absolute top-4 right-4 text-site-text-subtle hover:text-site-text-inverse transition-colors"
              data-testid="newsletter-modal-close"
            >
              <X className="w-5 h-5" />
            </button>
            
            <h3 className="font-heading text-2xl italic text-site-text-inverse mb-2">
              Stay in the loop
            </h3>
            <p className="font-body text-sm text-site-text-subtle mb-6">
              Subscribe for new drops, style inspo, and exclusive Amazon finds.
            </p>
            
            <form onSubmit={handleNewsletterSubmit}>
              <input
                type="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                placeholder="Enter your email"
                className="w-full bg-transparent border border-zinc-800 text-site-text-inverse px-4 py-3 font-body text-sm mb-4 focus:outline-none focus:border-site-text-inverse transition-colors placeholder:text-site-text-muted"
                data-testid="newsletter-email-input"
              />
              <button
                type="submit"
                disabled={isSubmitting}
                className="w-full bg-site-bg text-site-text py-3 font-mono text-xs uppercase tracking-widest hover:bg-site-border transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                data-testid="newsletter-submit-btn"
              >
                {isSubmitting ? 'Subscribing...' : 'Subscribe'}
              </button>
            </form>
          </div>
        </div>
      )}
    </footer>
  );
};

export default Footer;
